import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { cn } from './cn.js';
import { CURRICULUM_CEFR_LEVELS } from '@speaking-coach/shared-types';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
// Level explanations are mutually exclusive, including hover after a click.
// Keep this coordination local to level help, independent of other popovers.
let activeLevelHelp = null;
const listeners = new Set();
function subscribe(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
function setActiveLevelHelp(id) {
    if (activeLevelHelp === id)
        return;
    activeLevelHelp = id;
    listeners.forEach(listener => listener());
}
function closeLevelHelp(id) {
    // A delayed close from the previous trigger must not close its replacement.
    if (activeLevelHelp === id)
        setActiveLevelHelp(null);
}
const getActiveLevelHelp = () => activeLevelHelp;
const getServerLevelHelp = () => null;
const LevelBadgeContext = createContext(null);
/** Related levels share one panel: switching updates its content in place. */
export function LevelBadgeGroup({ children, anchor }) {
    const groupId = useId();
    const [triggerId, setTriggerId] = useState(null);
    const open = useSyncExternalStore(subscribe, getActiveLevelHelp, getServerLevelHelp) === groupId;
    const dismissed = useRef(false);
    useEffect(() => () => closeLevelHelp(groupId), [groupId]);
    return (_jsx(LevelBadgeContext.Provider, { value: {
            openTriggerId: open ? triggerId : null,
            openFromFocus: (id) => {
                if (dismissed.current)
                    return;
                setTriggerId(id);
                setActiveLevelHelp(groupId);
            },
            resetDismissed: () => { dismissed.current = false; },
        }, children: _jsx(Popover, { triggerId: triggerId, open: open, onOpenChange: (next, details) => {
                // Explicit dismissal suppresses restored focus, not a later keyboard
                // visit after hover-out or focus-out.
                dismissed.current = !next && (details.reason === 'escape-key' || details.reason === 'trigger-press' || details.reason === 'close-press');
                if (next) {
                    if (details.trigger)
                        setTriggerId(details.trigger.id);
                    setActiveLevelHelp(groupId);
                }
                else
                    closeLevelHelp(groupId);
            }, children: ({ payload }) => _jsxs(_Fragment, { children: [children, payload && _jsx(LevelHelpContent, { ...payload, anchor: anchor })] }) }) }));
}
/** Informational level help. Choosing/saving a level remains the host's action. */
export function LevelBadge(props) {
    const group = useContext(LevelBadgeContext);
    return group
        ? _jsx(LevelBadgeTrigger, { ...props })
        : _jsx(LevelBadgeGroup, { children: _jsx(LevelBadgeTrigger, { ...props }) });
}
function LevelBadgeTrigger(props) {
    const { level, label, className, children, inline = false } = props;
    const group = useContext(LevelBadgeContext);
    const triggerId = useId();
    const pointerFocus = useRef(false);
    const isRadio = children?.props?.role === 'radio';
    return (_jsx(PopoverTrigger, { id: triggerId, payload: props, "data-level-help": level, "data-testid": props['data-testid'], "aria-label": props['aria-label'], asChild: !!children, "aria-expanded": isRadio ? undefined : group.openTriggerId === triggerId, openOnHover: true, delay: 250, closeDelay: 200, onPointerDown: () => { pointerFocus.current = true; }, onFocus: () => {
            if (!pointerFocus.current)
                group.openFromFocus(triggerId);
            pointerFocus.current = false;
        }, onBlur: () => { pointerFocus.current = false; group.resetDismissed(); }, className: children ? '' : cn('pointer-coarse:min-h-11 pointer-coarse:min-w-11 cursor-help font-semibold text-text-primary focus-visible:focus-ring', inline
            ? 'inline-block text-left align-baseline border-0 bg-transparent p-0 rounded-token-input-sm text-inherit [font:inherit]'
            : 'inline-flex items-center gap-1 rounded-full border border-border-default bg-surface-card px-3 py-1 text-caption', className), children: children ?? label ?? level }));
}
function LevelHelpContent({ level, name, description, href, learnMoreLabel, positionLabel, renderLink, anchor }) {
    const curriculumLevels = CURRICULUM_CEFR_LEVELS;
    const levelIndex = curriculumLevels.indexOf(level);
    const titleId = useId();
    const descriptionId = useId();
    const linkProps = {
        href,
        className: 'ml-auto flex min-h-6 w-fit items-center pointer-coarse:min-h-11 text-caption font-medium text-text-secondary underline decoration-border-default underline-offset-4 hover:text-text-primary hover:decoration-current focus-visible:focus-ring',
        children: learnMoreLabel,
    };
    return (_jsxs(PopoverContent, { "aria-labelledby": titleId, "aria-describedby": descriptionId, initialFocus: false, 
        // Replacing an explanation must not steal focus back from the new one.
        finalFocus: () => activeLevelHelp === null, anchor: anchor, className: "w-72 max-sm:w-[calc(100vw-3*var(--page-gutter))] max-w-[calc(100vw-3*var(--page-gutter))] p-4 font-learner text-body leading-normal", sideOffset: 8, collisionPadding: 24, children: [_jsxs("div", { className: "flex items-center justify-between gap-3", children: [_jsxs("p", { id: titleId, className: "m-0 font-semibold text-text-primary", children: [level, " \u2014 ", name] }), levelIndex >= 0 && (_jsx("span", { role: "img", "aria-label": positionLabel(levelIndex + 1, curriculumLevels.length), className: "flex shrink-0 gap-1", children: curriculumLevels.map((item, index) => (_jsx("span", { className: cn('size-2 rounded-full', index <= levelIndex ? 'bg-accent' : 'bg-border-default') }, item))) }))] }), _jsx("p", { id: descriptionId, className: "mt-2 mb-3 text-text-secondary", children: description }), renderLink ? renderLink(linkProps) : _jsx("a", { ...linkProps })] }));
}
//# sourceMappingURL=level-badge.js.map